import { useAuthStore } from "@/auth/authStore";
import { useNavigate } from "react-router-dom";
import { PageBanner } from "@/shared/components/PageBanner";
import {
  useV1InventoryMaterialsList,
  useV1InventoryWarehousesList,
  useV1InventoryUomList,
  useV1ProductionProductionOrdersList
} from "@/api/generated/v1/v1";

export function DashboardPage() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();

  const hasInvPerm = user?.permissions?.includes("inventory.view") || user?.roles?.includes("admin");
  const hasProdPerm = user?.permissions?.includes("production.view") || user?.roles?.includes("admin");
  const hasQualPerm = user?.permissions?.includes("quality.view") || user?.roles?.includes("admin");
  const hasPlanPerm = user?.permissions?.includes("planning.view") || user?.roles?.includes("admin");

  const { data: materialsData, isFetching: isFetchingMaterials } = useV1InventoryMaterialsList(undefined, { query: { enabled: hasInvPerm } });
  const { data: warehousesData, isFetching: isFetchingWarehouses } = useV1InventoryWarehousesList(undefined, { query: { enabled: hasInvPerm } });
  const { data: uomData, isFetching: isFetchingUom } = useV1InventoryUomList(undefined, { query: { enabled: hasInvPerm } });
  const { data: prodOrdersData, isFetching: isFetchingProdOrders } = useV1ProductionProductionOrdersList(undefined, { query: { enabled: hasProdPerm } });

  const formatValue = (data: any, isFetching: boolean) => isFetching ? "..." : (data?.count?.toLocaleString('fa-IR') ?? "۰");
  const formatChange = (isFetching: boolean) => isFetching ? "در حال بروزرسانی" : "زنده";

  const stats = [
    { label: "خانواده متریال‌ها", value: formatValue(materialsData, isFetchingMaterials), change: formatChange(isFetchingMaterials), icon: "📦", color: "var(--color-primary)", path: "/inventory/materials" },
    { label: "انبارهای فعال", value: formatValue(warehousesData, isFetchingWarehouses), change: formatChange(isFetchingWarehouses), icon: "🏭", color: "#10B981", path: "/inventory/warehouses" },
    { label: "واحدهای اندازه‌گیری", value: formatValue(uomData, isFetchingUom), change: formatChange(isFetchingUom), icon: "📐", color: "#F59E0B", path: "/inventory/uom" },
    { label: "سفارشات تولید جاری", value: formatValue(prodOrdersData, isFetchingProdOrders), change: formatChange(isFetchingProdOrders), icon: "⚙️", color: "#8B5CF6", path: "/production" }
  ];

  const ordersLinesLinks = [
    { title: "خطوط و دستگاه‌ها", desc: "مدیریت خطوط تولید، ایستگاه‌ها و ماشین‌آلات", path: "/production/lines", icon: "🏭" },
    { title: "لیست سفارشات تولید", desc: "مشاهده و مدیریت وضعیت سفارشات و کارهای تولیدی", path: "/production/orders", icon: "📋" },
    { title: "سفارش تولید جدید", desc: "ایجاد سفارش تولید جدید بر اساس BOM و Routing", path: "/production/orders/create", icon: "⚡" },
    { title: "مسیریابی تولید (Routing)", desc: "تعریف و مدیریت ایستگاه‌ها و مراحل تولید محصول", path: "/routing", icon: "🗺️" }
  ];

  const bomLinks = [
    { title: "خانواده‌های BOM", desc: "مشاهده و مدیریت درختواره مواد محصول", path: "/bom/families", icon: "📋" },
    { title: "ایجاد خانواده BOM", desc: "تعریف BOM پایه برای یک محصول جدید", path: "/bom/families/create", icon: "🏗️" },
    { title: "تخصیص قطعات به ایستگاه‌ها", desc: "ارتباط قطعات فرمول ساخت با ایستگاه‌های کاری تولید", path: "/bom/allocations", icon: "🔗" },
    { title: "فیلدهای سفارشی", desc: "تعریف ویژگی‌های داینامیک برای موجودیت‌ها", path: "/forms/custom-fields", icon: "🏷️" }
  ];

  const shopfloorLinks = [
    { title: "توقفات تجهیزات", desc: "ثبت و پیگیری توقفات خط تولید و دلایل آن", path: "/production/shopfloor/downtime", icon: "⏸️" },
    { title: "دستورالعمل‌های کاری", desc: "دستورالعمل‌ها و راهنماهای اجرایی عملیات تولید", path: "/production/shopfloor/work-instructions", icon: "📄" },
    { title: "مدیریت رسترها", desc: "تعریف الگوهای چرخشی شیفت (Roster)", path: "/production/shopfloor/rosters", icon: "📅" },
    { title: "تخصیص شیفت/رستر", desc: "تعریف افراد به رسترها و اورراید شیفت", path: "/production/shopfloor/roster-assignments", icon: "👥" },
    { title: "درخواست‌های مرخصی", desc: "ثبت و تأیید درخواست‌های مرخصی", path: "/production/shopfloor/leave-requests", icon: "🏖️" },
    { title: "جابجایی شیفت‌ها", desc: "درخواست جابجایی شیفت بین اپراتورها", path: "/production/shopfloor/shift-swaps", icon: "🔄" },
    { title: "تخصیص ایستگاه/دستگاه", desc: "تخصیص اپراتور به یک یا چند دستگاه", path: "/production/station-assignments", icon: "🏭" },
  ];

  const materialHoldLinks = [
    { title: "مصرف قطعات", desc: "ثبت و گزارش‌گیری مصرف متریال در سفارشات تولید", path: "/production/consumption", icon: "🔧" },
    { title: "ضایعات تولید", desc: "ثبت و ردگیری قطعات ضایعاتی با کدهای علت", path: "/production/scrap", icon: "🗑️" },
    { title: "انبار ناقصی‌ها (Hold)", desc: "مدیریت و ثبت توقف کارهای تولیدی به دلیل کمبود قطعه", path: "/production/shortage-holds", icon: "📦" },
    { title: "پیش‌خوان کیفیت (QC)", desc: "مشاهده وضعیت بازرسی‌ها و استانداردهای کیفیت", path: "/quality", icon: "🛡️" }
  ];

  const serializationLinks = [
    { title: "ثبت سریال قطعه", desc: "ثبت و اسکن بارکد سریال قطعات و محصولات تولیدی", path: "/production/serialization/register", icon: "🏷️" },
    { title: "سشن‌های پالت ایستگاه", desc: "باز و بستن سشن تغذیه پالت روی ایستگاه‌های کاری", path: "/production/serialization/feed-sessions", icon: "📦" },
    { title: "ردیابی کامل سریال", desc: "مشاهده درخت نسب و پالت‌های مستقیم مرتبط با یک سریال", path: "/production/serialization/trace", icon: "🔍" },
    { title: "محصولات متأثر (Recall)", desc: "شناسایی تمام قطعات و محصولات متاثر از یک پالت آلوده", path: "/production/serialization/affected-units", icon: "⚠️" }
  ];

  const inventoryLinks = [
    { title: "پیش‌خوان انبار", desc: "نمای کلی موجودی‌ها و تراکنش‌های انبار", path: "/inventory", icon: "📊" },
    { title: "خانواده متریال‌ها", desc: "تعریف کدهای جدید کالا و متریال در پایگاه داده", path: "/inventory/materials", icon: "🏷️" },
    { title: "انبارها و مکان‌ها", desc: "ایجاد انبار و تعریف راهروها و قفسه‌های فیزیکی", path: "/inventory/warehouses", icon: "🏭" },
    { title: "تراکنش‌های موجودی", desc: "مشاهده و مدیریت تراکنش‌های ورود/خروج موجودی", path: "/inventory/stock", icon: "⚖️" },
    { title: "پالت‌ها (Lots)", desc: "ردیابی و مدیریت پالت‌های موجودی", path: "/inventory/lots", icon: "📦" }
  ];

  const importLabelLinks = [
    { title: "ورود اکسل متریال", desc: "بارگذاری انبوه کاتالوگ متریال‌ها از فایل اکسل", path: "/inventory/materials/import", icon: "📥" },
    { title: "ورود اکسل رسید انبار", desc: "ثبت گروهی تراکنش‌های رسید انبار با فایل اکسل", path: "/inventory/stock-receipts/import", icon: "📋" },
    { title: "تنظیمات برچسب", desc: "مدیریت ابعاد و قالب‌های چاپ برچسب", path: "/inventory/label-settings", icon: "🖨️" },
    { title: "چاپ گروهی برچسب", desc: "تولید و چاپ دسته‌ای برچسب‌های پالت", path: "/inventory/print-batch", icon: "📑" }
  ];

  const planningLinks = [
    { title: "پیش‌خوان برنامه‌ریزی", desc: "داشبورد کلان برنامه‌ریزی تولید", path: "/planning", icon: "📊" },
    { title: "برنامه جامع تولید (MPS)", desc: "مدیریت دوره‌های برنامه‌ریزی تولید", path: "/planning/master-schedules", icon: "🗓️" },
    { title: "اجراهای برنامه مواد (MRP)", desc: "محاسبه و اجرای نیازمندی‌های مواد", path: "/planning/mrp-runs", icon: "⚙️" },
    { title: "بررسی ظرفیت (Capacity)", desc: "محاسبه و کنترل ظرفیت ایستگاه‌های کاری", path: "/planning/capacity-check", icon: "⚖️" }
  ];

  const qualityLinks = [
    { title: "پیش‌خوان کیفیت", desc: "مشاهده شاخص‌های کلیدی و خلاصه‌وضعیت کیفیت", path: "/quality", icon: "📊" },
    { title: "خانواده‌های چک‌لیست کیفیت", desc: "تعریف استانداردهای بازرسی و چک‌لیست‌های QC", path: "/quality/families", icon: "📑" },
    { title: "سوابق بازرسی", desc: "مشاهده لیست نتایج و فرم‌های بازرسی انجام‌شده", path: "/quality/inspections", icon: "🔍" },
    { title: "ثبت بازرسی جدید", desc: "ارزیابی و ثبت نتیجه بازرسی کیفیت جدید", path: "/quality/inspections/new", icon: "➕" }
  ];

  const formsWorkflowReportsLinks = [
    { title: "خانواده‌های فرم", desc: "مدیریت و طراحی قالب‌های پویا", path: "/forms/families", icon: "📑" },
    { title: "فرم‌های پر شده", desc: "مشاهده دیتا و چک‌لیست‌های ثبت شده", path: "/forms/instances", icon: "📥" },
    { title: "صندوق تأیید (Inbox)", desc: "کارتابل اقدامات و تأییدیه‌های معوق کاربر", path: "/workflow/inbox", icon: "📥" },
    { title: "تعاریف گزارش‌ها", desc: "مشاهده لیست گزارش‌های ساخت‌یافته و پویا", path: "/reporting", icon: "📊" },
    { title: "طراحی قالب چاپ", desc: "طراحی فرمت‌های خروجی و گزارش‌های چاپی (PDF)", path: "/printing", icon: "🖼️" }
  ];

  const marketplaceOrgLinks = [
    { title: "پیش‌خوان بازارچه", desc: "مشاهده ویترین و داشبورد بازارچه", path: "/marketplace", icon: "🏪" },
    { title: "آگهی‌های فروش", desc: "مدیریت و مشاهده آگهی‌های عرضه کالا", path: "/marketplace/listings", icon: "📢" },
    { title: "کاتالوگ و فروشگاه‌ها", desc: "مدیریت کاتالوگ‌ها و فروشگاه‌های آنلاین", path: "/marketplace/catalog", icon: "📚" },
    { title: "پیکربندی سازمان", desc: "تنظیمات سازمان فعال، اعضا و سطوح دسترسی سیستم", path: "/organization/hierarchy", icon: "🏢" }
  ];

  const supportLinks = [
    { title: "ثبت تیکت پشتیبانی", desc: "ثبت گزارش باگ، درخواست ویژگی یا مسأله‌ی جدید", path: "/support/tickets/new", icon: "🎫" },
    { title: "کارتابل پشتیبانی", desc: "مدیریت و مشاهده درخواست‌های پشتیبانی از راه دور", path: "/remote-assist", icon: "🤝" },
  ];

  return (
    <div style={{ padding: "0.5rem 0", maxWidth: "100%", margin: "0" }}>
      <style>{`
        .kpi-card {
          padding: 0.5rem 0.5rem 0.5rem 4rem;
          min-height: 110px;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          border-radius: 16px;
          display: flex;
          flex-direction: row;
          align-items: center;
          gap: 0.1rem;
          position: relative;
          overflow: hidden;
        }
        .kpi-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.2);
          border-color: rgba(255, 255, 255, 0.2);
        }
        .kpi-card::after {
          content: "";
          position: absolute;
          top: -50%;
          right: -50%;
          width: 150px;
          height: 150px;
          background: radial-gradient(circle, rgba(255,255,255,0.03) 0%, transparent 70%);
          border-radius: 50%;
        }
        .quick-action-card {
          padding: 0.5rem 0.1rem;
          cursor: pointer;
          transition: all 0.25s ease;
          border-radius: 14px;
          background: rgba(255, 255, 255, 0.02);
          border: 1px solid rgba(255, 255, 255, 0.05);
        }
        .quick-action-card:hover {
          background: rgba(255, 255, 255, 0.06);
          border-color: var(--color-primary);
          transform: translateX(-4px);
        }
      `}</style>

      <PageBanner
        title={`سلام، ${user?.username ?? user?.email}!`}
        subtitle="خوش آمدید. به سامانه مدیریت منابع تولید کارخانه میمزاد دسترسی دارید. از نوار ابزار کنار یا بخش زیر می‌توانید ماژول‌های سیستم را مدیریت کنید."
      />
      {/* Stats Grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: "1.5rem",
          marginBottom: "2rem"
        }}
      >
        {stats.map((item, idx) => (
          <div
            key={idx}
            className="brand-card kpi-card"
            onClick={() => navigate(item.path)}
          >
            <span style={{ fontSize: "1.8rem", background: "rgba(255,255,255,0.05)", width: "45px", height: "45px", borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              {item.icon}
            </span>
            <div style={{ display: "flex", flexDirection: "column", gap: "1rem", flex: 1 }}>
              <span style={{ fontSize: "1.1rem", fontWeight: "700", color: "var(--color-text-main, #fff)" }}>
                {item.label}
              </span>
              <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: "1.1rem", fontWeight: "800", color: "#007270", lineHeight: 1 }}>
                  {item.value}
                </span>
                <span style={{ fontSize: "0.85rem", fontWeight: "600", color: item.color }}>
                  {item.change}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions & System Info Grid */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: "1rem" }}>
        {/* Card 1: Orders & Lines */}
        {hasProdPerm && (
          <div className="brand-card-extand" style={{ padding: "1.5rem" }}>
            <h2 className="text-lg font-bold mb-4" style={{ display: "flex", alignItems: "center", gap: "0.5rem", color: "var(--color-text-main)" }}>
              <span>⚡</span> سفارشات و خطوط تولید
            </h2>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              {ordersLinesLinks.map((link, idx) => (
                <div key={idx} className="quick-action-card" onClick={() => navigate(link.path)}>
                  <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                    <div style={{ fontSize: "1.4rem" }}>{link.icon}</div>
                    <div>
                      <h3 style={{ fontSize: "0.95rem", fontWeight: "700", margin: "0 0 0.25rem 0", color: "#007270" }}>{link.title}</h3>
                      <p style={{ fontSize: "0.8rem", color: "var(--color-text-muted)", margin: "0" }}>{link.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Card 2: Product Engineering & BOM */}
        {hasProdPerm && (
          <div className="brand-card-extand" style={{ padding: "1.5rem" }}>
            <h2 className="text-lg font-bold mb-4" style={{ display: "flex", alignItems: "center", gap: "0.5rem", color: "var(--color-text-main)" }}>
              <span>🛠️</span> مهندسی محصول و BOM
            </h2>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              {bomLinks.map((link, idx) => (
                <div key={idx} className="quick-action-card" onClick={() => navigate(link.path)}>
                  <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                    <div style={{ fontSize: "1.4rem" }}>{link.icon}</div>
                    <div>
                      <h3 style={{ fontSize: "0.95rem", fontWeight: "700", margin: "0 0 0.25rem 0", color: "#007270" }}>{link.title}</h3>
                      <p style={{ fontSize: "0.8rem", color: "var(--color-text-muted)", margin: "0" }}>{link.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Card 3: Shopfloor & Shifts */}
        {hasProdPerm && (
          <div className="brand-card-extand" style={{ padding: "1.5rem" }}>
            <h2 className="text-lg font-bold mb-4" style={{ display: "flex", alignItems: "center", gap: "0.5rem", color: "var(--color-text-main)" }}>
              <span>⏸️</span> اجرای کارگاه و شیفت‌ها
            </h2>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              {shopfloorLinks.map((link, idx) => (
                <div key={idx} className="quick-action-card" onClick={() => navigate(link.path)}>
                  <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                    <div style={{ fontSize: "1.4rem" }}>{link.icon}</div>
                    <div>
                      <h3 style={{ fontSize: "0.95rem", fontWeight: "700", margin: "0 0 0.25rem 0", color: "#007270" }}>{link.title}</h3>
                      <p style={{ fontSize: "0.8rem", color: "var(--color-text-muted)", margin: "0" }}>{link.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Card 4: Material Control & Holds */}
        {hasProdPerm && (
          <div className="brand-card-extand" style={{ padding: "1.5rem" }}>
            <h2 className="text-lg font-bold mb-4" style={{ display: "flex", alignItems: "center", gap: "0.5rem", color: "var(--color-text-main)" }}>
              <span>🔧</span> مصرف قطعات و ناقصی‌ها
            </h2>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              {materialHoldLinks.map((link, idx) => (
                <div key={idx} className="quick-action-card" onClick={() => navigate(link.path)}>
                  <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                    <div style={{ fontSize: "1.4rem" }}>{link.icon}</div>
                    <div>
                      <h3 style={{ fontSize: "0.95rem", fontWeight: "700", margin: "0 0 0.25rem 0", color: "#007270" }}>{link.title}</h3>
                      <p style={{ fontSize: "0.8rem", color: "var(--color-text-muted)", margin: "0" }}>{link.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Card 5: Serialization & Genealogy */}
        {hasProdPerm && (
          <div className="brand-card-extand" style={{ padding: "1.5rem" }}>
            <h2 className="text-lg font-bold mb-4" style={{ display: "flex", alignItems: "center", gap: "0.5rem", color: "var(--color-text-main)" }}>
              <span>🏷️</span> سریال‌سازی و ردگیری
            </h2>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              {serializationLinks.map((link, idx) => (
                <div key={idx} className="quick-action-card" onClick={() => navigate(link.path)}>
                  <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                    <div style={{ fontSize: "1.4rem" }}>{link.icon}</div>
                    <div>
                      <h3 style={{ fontSize: "0.95rem", fontWeight: "700", margin: "0 0 0.25rem 0", color: "#007270" }}>{link.title}</h3>
                      <p style={{ fontSize: "0.8rem", color: "var(--color-text-muted)", margin: "0" }}>{link.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Card 6: Inventory Management */}
        {hasInvPerm && (
          <div className="brand-card-extand" style={{ padding: "1.5rem" }}>
            <h2 className="text-lg font-bold mb-4" style={{ display: "flex", alignItems: "center", gap: "0.5rem", color: "var(--color-text-main)" }}>
              <span>📦</span> مدیریت انبارداری
            </h2>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              {inventoryLinks.map((link, idx) => (
                <div key={idx} className="quick-action-card" onClick={() => navigate(link.path)}>
                  <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                    <div style={{ fontSize: "1.4rem" }}>{link.icon}</div>
                    <div>
                      <h3 style={{ fontSize: "0.95rem", fontWeight: "700", margin: "0 0 0.25rem 0", color: "#007270" }}>{link.title}</h3>
                      <p style={{ fontSize: "0.8rem", color: "var(--color-text-muted)", margin: "0" }}>{link.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Card 7: Import & Labeling Tools */}
        {hasInvPerm && (
          <div className="brand-card-extand" style={{ padding: "1.5rem" }}>
            <h2 className="text-lg font-bold mb-4" style={{ display: "flex", alignItems: "center", gap: "0.5rem", color: "var(--color-text-main)" }}>
              <span>🖨️</span> ابزارهای ورودی و برچسب
            </h2>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              {importLabelLinks.map((link, idx) => (
                <div key={idx} className="quick-action-card" onClick={() => navigate(link.path)}>
                  <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                    <div style={{ fontSize: "1.4rem" }}>{link.icon}</div>
                    <div>
                      <h3 style={{ fontSize: "0.95rem", fontWeight: "700", margin: "0 0 0.25rem 0", color: "#007270" }}>{link.title}</h3>
                      <p style={{ fontSize: "0.8rem", color: "var(--color-text-muted)", margin: "0" }}>{link.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Card 8: Production Planning */}
        {hasPlanPerm && (
          <div className="brand-card-extand" style={{ padding: "1.5rem" }}>
            <h2 className="text-lg font-bold mb-4" style={{ display: "flex", alignItems: "center", gap: "0.5rem", color: "var(--color-text-main)" }}>
              <span>📅</span> برنامه‌ریزی تولید
            </h2>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              {planningLinks.map((link, idx) => (
                <div key={idx} className="quick-action-card" onClick={() => navigate(link.path)}>
                  <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                    <div style={{ fontSize: "1.4rem" }}>{link.icon}</div>
                    <div>
                      <h3 style={{ fontSize: "0.95rem", fontWeight: "700", margin: "0 0 0.25rem 0", color: "#007270" }}>{link.title}</h3>
                      <p style={{ fontSize: "0.8rem", color: "var(--color-text-muted)", margin: "0" }}>{link.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Card 9: Quality Control */}
        {hasQualPerm && (
          <div className="brand-card-extand" style={{ padding: "1.5rem" }}>
            <h2 className="text-lg font-bold mb-4" style={{ display: "flex", alignItems: "center", gap: "0.5rem", color: "var(--color-text-main)" }}>
              <span>🛡️</span> کنترل کیفیت (QC)
            </h2>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              {qualityLinks.map((link, idx) => (
                <div key={idx} className="quick-action-card" onClick={() => navigate(link.path)}>
                  <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                    <div style={{ fontSize: "1.4rem" }}>{link.icon}</div>
                    <div>
                      <h3 style={{ fontSize: "0.95rem", fontWeight: "700", margin: "0 0 0.25rem 0", color: "#007270" }}>{link.title}</h3>
                      <p style={{ fontSize: "0.8rem", color: "var(--color-text-muted)", margin: "0" }}>{link.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Card 10: Forms, Workflow & Reports */}
        <div className="brand-card-extand" style={{ padding: "1.5rem" }}>
          <h2 className="text-lg font-bold mb-4" style={{ display: "flex", alignItems: "center", gap: "0.5rem", color: "var(--color-text-main)" }}>
            <span>📝</span> فرم‌ها، کارتابل و گزارش‌ها
          </h2>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            {formsWorkflowReportsLinks.map((link, idx) => (
              <div key={idx} className="quick-action-card" onClick={() => navigate(link.path)}>
                <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                  <div style={{ fontSize: "1.4rem" }}>{link.icon}</div>
                  <div>
                    <h3 style={{ fontSize: "0.95rem", fontWeight: "700", margin: "0 0 0.25rem 0", color: "#007270" }}>{link.title}</h3>
                    <p style={{ fontSize: "0.8rem", color: "var(--color-text-muted)", margin: "0" }}>{link.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Card 11: Marketplace & Organization */}
        <div className="brand-card-extand" style={{ padding: "1.5rem" }}>
          <h2 className="text-lg font-bold mb-4" style={{ display: "flex", alignItems: "center", gap: "0.5rem", color: "var(--color-text-main)" }}>
            <span>🛒</span> بازارچه و پیکربندی سازمان
          </h2>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            {marketplaceOrgLinks.map((link, idx) => (
              <div key={idx} className="quick-action-card" onClick={() => navigate(link.path)}>
                <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                  <div style={{ fontSize: "1.4rem" }}>{link.icon}</div>
                  <div>
                    <h3 style={{ fontSize: "0.95rem", fontWeight: "700", margin: "0 0 0.25rem 0", color: "#007270" }}>{link.title}</h3>
                    <p style={{ fontSize: "0.8rem", color: "var(--color-text-muted)", margin: "0" }}>{link.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Card 12: Support & Help */}
        <div className="brand-card-extand" style={{ padding: "1.5rem" }}>
          <h2 className="text-lg font-bold mb-4" style={{ display: "flex", alignItems: "center", gap: "0.5rem", color: "var(--color-text-main)" }}>
            <span>🎧</span> پشتیبانی و راهنما
          </h2>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            {supportLinks.map((link, idx) => (
              <div key={idx} className="quick-action-card" onClick={() => navigate(link.path)}>
                <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                  <div style={{ fontSize: "1.4rem" }}>{link.icon}</div>
                  <div>
                    <h3 style={{ fontSize: "0.95rem", fontWeight: "700", margin: "0 0 0.25rem 0", color: "#007270" }}>{link.title}</h3>
                    <p style={{ fontSize: "0.8rem", color: "var(--color-text-muted)", margin: "0" }}>{link.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* System Logs / Info */}
        <div className="brand-card-extand" style={{ padding: "2rem" }}>
          <h2 className="text-lg font-bold mb-4" style={{ display: "flex", alignItems: "center", gap: "0.5rem", color: "var(--color-text-main)" }}>
            <span>ℹ️</span> مشخصات محیط و دسترسی‌ها
          </h2>
          <div style={{ display: "flex", flexDirection: "column", gap: "1rem", fontSize: "0.9rem" }}>
            <div style={{ display: "flex", justifyContent: "space-between", borderBottom: "1px solid rgba(255,255,255,0.05)", paddingBottom: "0.5rem" }}>
              <span style={{ color: "var(--color-text-muted)" }}>کد کاربری:</span>
              <span style={{ fontFamily: "monospace" }}>{user?.id?.substring(0, 8)}...</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", borderBottom: "1px solid rgba(255,255,255,0.05)", paddingBottom: "0.5rem" }}>
              <span style={{ color: "var(--color-text-muted)" }}>وضعیت حساب:</span>
              <span style={{ color: "var(--color-success)", fontWeight: "bold" }}>فعال (Active)</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", borderBottom: "1px solid rgba(255,255,255,0.05)", paddingBottom: "0.5rem" }}>
              <span style={{ color: "var(--color-text-muted)" }}>نقش‌های انتساب داده شده:</span>
              <span style={{ direction: "ltr" }}>{user?.roles?.join(", ") || "—"}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: "var(--color-text-muted)" }}>سطح دسترسی ماژول:</span>
              <span>کامل (Platform Level)</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
